import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  step1: {
    name: "", //done
    day_start: "",
    day_end: "",
    transport_type_id: 1, //done
    // is_provider: false, // true if provider
    country_id: "", //done
    city_id: null, //done
    phone: "", //done
    address: "", //done
    // availability_start: "",
    // availability_end: "",
    transfers: [
      {
        transfer_name: "",
        origin_terminal_id: null,
        origin_terminal_type_id: null,
        destination_terminal_id: null,
        destination_terminal_type_id: null,
        transfer_time: 0,
        transfer_type_id: 1,
      },
    ],
  },
  step2: {
    short_summary: "",
    detailed_description: "",
    key_points: "",
    facilities: [],
    keyword: [],
    cars: [
      {
        car_type_id: null,
        car_model_id: null,
        car_brand_id: null,
        capacity_max: 0,
        waiting_time: 0,
        luggage_capacity: 0,
        description: "",
        equipment_ids: [],
      },
    ],
    transfer_ids: [],
  },
  step3: {
    cancellation_policy_id: null,
    booking_confirmation_mode: "",
    pricing: [
      {
        product_transfer_id: null,
        prices: [
          {
            car_id: null,
            day_price: 0,
            night_price: 0,
            is_place: false,
          },
        ],
      },
    ],
    charge_service: "5",
    charge_service_type: "out",
  },
};

function deepUpdate(obj, field, value) {
  if (obj && typeof obj === "object") {
    if (field in obj) {
      obj[field] = value;
      return true;
    }
    for (const key of Object.keys(obj)) {
      if (deepUpdate(obj[key], field, value)) {
        return true;
      }
    }
  }
  return false;
}

const transportSlice = createSlice({
  name: "transport",
  initialState,
  reducers: {
    updateTransportStepField: (state, action) => {
      const { field, value } = action.payload;

      if (typeof field !== "string") {
        console.error("updateTransportStepField: invalid field", field);
        return;
      }

      // Dot notation support
      if (field.includes(".")) {
        const keys = field.split(".");
        let target = state;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!target[keys[i]]) return;
          target = target[keys[i]];
        }
        target[keys[keys.length - 1]] = value;
        return;
      }

      // Deep search fallback
      if (!deepUpdate(state, field, value)) {
        console.warn(`Field '${field}' not found in state`);
      }
    },

    updateTransportStepBulk: (state, action) => {
      const { step, data } = action.payload;
      state[step] = { ...state[step], ...data };
    },

    setTransportProductId: (state, action) => {
      state.product_id = action.payload;
      state.step2.product_id = action.payload;
      state.step3.product_id = action.payload;
    },

    updateTransferField: (state, action) => {
      const { index, field, value } = action.payload;
      if (
        state.step1.transfers[index] &&
        field in state.step1.transfers[index]
      ) {
        state.step1.transfers[index][field] = value;
      }
    },

    // --- Transfers ---
    addTransfer: (state) => {
      state.step1.transfers.push({
        transfer_name: "",
        origin_terminal_id: null,
        origin_terminal_type_id: null,
        destination_terminal_id: null,
        destination_terminal_type_id: null,
        transfer_time: 0,
        transfer_type_id: state.step1.transport_type_id || null,
      });

      // also add to pricing
      state.step3.pricing.push({
        product_transfer_id: state.step1.transfers.length, // simple index for now
        prices: state.step2.cars.map((_, carIdx) => ({
          car_id: carIdx + 1, // or use real car_id from backend
          day_price: 0,
          night_price: 0,
          is_place: false,
        })),
      });
    },

    removeTransfer: (state, action) => {
      const index = action.payload;
      if (index >= 0 && index < state.step1.transfers.length) {
        state.step1.transfers.splice(index, 1);
        state.step3.pricing.splice(index, 1); // keep pricing aligned
      }
    },

    // --- Cars ---
    addCar: (state) => {
      state.step2.cars.push({
        car_type_id: null,
        car_model_id: null,
        car_brand_id: null,
        capacity_max: 0,
        waiting_time: 0,
        luggage_capacity: 0,
        description: "",
        equipment_ids: [],
      });

      // also update every transfer’s pricing
      state.step3.pricing.forEach((transfer, idx) => {
        transfer.prices.push({
          car_id: state.step2.cars.length, // or real car_id
          day_price: 0,
          night_price: 0,
          is_place: false,
        });
      });
    },

    removeCar: (state, action) => {
      const index = action.payload;
      if (index >= 0 && index < state.step2.cars.length) {
        state.step2.cars.splice(index, 1);
        // remove from pricing for every transfer
        state.step3.pricing.forEach((transfer) => {
          transfer.prices.splice(index, 1);
        });
      }
    },

    updateCarField: (state, action) => {
      const { index, field, value } = action.payload;
      if (state.step2.cars[index] && field in state.step2.cars[index]) {
        state.step2.cars[index][field] = value;
      }
    },

    // --- Pricing ---
    updatePricingField: (state, action) => {
      const { transfer_idx, car_idx, field, value } = action.payload;
      if (
        state.step3.pricing[transfer_idx] &&
        state.step3.pricing[transfer_idx].prices[car_idx]
      ) {
        state.step3.pricing[transfer_idx].prices[car_idx][field] = value;
      }
    },

    // NEW: apply current car prices to same car in all itineraries
    applyPricesOnCarAllItineraries: (state, action) => {
      const { car_idx, day_price, night_price } = action.payload;
      state.step3.pricing.forEach((transfer) => {
        if (transfer?.prices?.[car_idx]) {
          transfer.prices[car_idx].day_price = day_price;
          transfer.prices[car_idx].night_price = night_price;
        }
      });
    },

    // NEW: apply current car prices to all cars in this itinerary
    applyPricesOnAllCarsInItinerary: (state, action) => {
      const { transfer_idx, day_price, night_price } = action.payload;
      const transfer = state.step3.pricing[transfer_idx];
      if (transfer?.prices) {
        transfer.prices.forEach((carPrice) => {
          carPrice.day_price = day_price;
          carPrice.night_price = night_price;
        });
      }
    },

    resetTransport: () => initialState,
  },
});

export const {
  updateTransportStepField,
  updateTransportStepBulk,
  setTransportProductId,
  resetTransport,
  addTransfer,
  removeTransfer,
  updateTransferField,
  addCar,
  removeCar,
  updatePricingField,
  updateCarField,
  applyPricesOnCarAllItineraries,
  applyPricesOnAllCarsInItinerary,
} = transportSlice.actions;

export default transportSlice.reducer;
